// OrderEditExistingProductForm.js
import React, { useState, useEffect } from 'react';
import './OrderEditExistingProduct.css';

const OrderEditExistingProductForm = ({ product, productId, handleSaveProduct }) => {
  // Use local state for all editable fields so default values are preserved.
  // These fields are merged from the default product data (productId) and
  // any fetched details from "product" (if applicable).
  const [formData, setFormData] = useState({
    id: null,
    ProductId: "", // Must be set to uniquely identify the product
    Name: "",
    Color: "",
    Size: "",
    Quantity: 1,
    // Imprinting related fields
    imprinting: "Unimprinted", // defaults to Unimprinted if none selected
    imprinting_color: "",
    imprinting_size: "",
    imprinting_location: "",
    imprinting_decoration_instructions: "",
    imprinting_setup_fee: 0,
    imprinting_logo_color_count: 0,
    imprinting_logo: null,
    additionalProductDetails: "",
  });

  // Initialize local formData when productId (default product data) is available/updated.
  useEffect(() => {
    if (productId) {
      setFormData({
        id: productId.id || productId.ProductId || null,
        ProductId: productId.ProductId || productId.id || "",
        Name: productId.Name || "",
        Color: productId.Color || "",
        Size: productId.Size || "",
        Quantity: productId.Quantity || 1,
        imprinting: productId.imprinting || "Unimprinted",
        imprinting_color: productId.imprinting_color || "",
        imprinting_size: productId.imprinting_size || "",
        imprinting_location: productId.imprinting_location || "",
        imprinting_decoration_instructions: productId.imprinting_decoration_instructions || "",
        imprinting_setup_fee: productId.imprinting_setup_fee || 0,
        imprinting_logo_color_count: productId.imprinting_logo_color_count || 0,
        imprinting_logo: productId.imprinting_logo || null,
        additionalProductDetails: productId.additionalProductDetails || "",
      });
    }
  }, [productId]);

  // Update local state on input changes.
  const onFieldChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  // If product (fetched details) isn't loaded yet, you can return a loading indicator.
  if (!product) {
    return null;
  }

  return (
    <div className="product-customization">
      <h3>Edit Product</h3>

      {/* Display Product Name (read-only) */}
      <div className="form-group">
        <label>Product Name:</label>
        <span>{formData.Name}</span>
      </div>

      {/* Color Selection */}
      <div className="form-group">
        <label>Color:</label>
        {product.Attributes?.Colors ? (
          <select
            value={formData.Color}
            onChange={(e) => onFieldChange("Color", e.target.value)}
          >
            <option value="">Select a color</option>
            {product.Attributes.Colors.Values.map((color) => (
              <option key={color.SKU} value={color.Name}>
                {color.Name}
              </option>
            ))}
          </select>
        ) : (
          <span>This product inherited the only color it offers.</span>
        )}
      </div>

      {/* Size Selection */}
      <div className="form-group">
        <label>Size:</label>
        {product.Attributes?.Sizes ? (
          <select
            value={formData.Size}
            onChange={(e) => onFieldChange("Size", e.target.value)}
          >
            <option value="">Select a size</option>
            {product.Attributes.Sizes.Values.map((size) => (
              <option key={size.Code} value={size.Code}>
                {size.Name}
              </option>
            ))}
          </select>
        ) : (
          <span>This product inherited the only size it offers.</span>
        )}
      </div>

      {/* Quantity Input */}
      <div className="form-group">
        <label>Quantity:</label>
        <input
          type="number"
          value={formData.Quantity}
          min="1"
          onChange={(e) =>
            onFieldChange("Quantity", parseInt(e.target.value, 10))
          }
        />
      </div>

      <hr />

      {/* Imprinting Method Selection */}
      <div className="form-group">
        <label>Imprinting method:</label>
        <select
          value={formData.imprinting}
          onChange={(e) => onFieldChange("imprinting", e.target.value)}
        >
          <option value="">Please select an imprinting method</option>
          <option value="Unimprinted">Unimprinted</option>
          {product.Imprinting?.Methods?.Values.map((method) => (
            <option key={method.VendorCode} value={method.Name}>
              {method.Name}
            </option>
          ))}
        </select>
      </div>

      {/* Show extra imprinting fields if a method other than "Unimprinted" is selected */}
      {formData.imprinting && formData.imprinting !== "Unimprinted" && (
        <>
          <div className="imprinting-options">
            <h4>Imprint Details</h4>
            <div className="form-group">
              <label>Imprinting Color:</label>
              <select
                value={formData.imprinting_color}
                onChange={(e) =>
                  onFieldChange("imprinting_color", e.target.value)
                }
              >
                <option value="">Select an imprinting color</option>
                {product.Imprinting?.Colors?.Values.map((color) => (
                  <option key={color.VendorCode} value={color.Code}>
                    {color.Name}
                  </option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label>Imprinting Size:</label>
              <select
                value={formData.imprinting_size}
                onChange={(e) =>
                  onFieldChange("imprinting_size", e.target.value)
                }
              >
                <option value="">Select an imprinting size</option>
                {product.Imprinting?.Sizes?.Values.map((size) => (
                  <option key={size.Code} value={size.Code}>
                    {size.Name}
                  </option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label>Imprinting Location:</label>
              <select
                value={formData.imprinting_location}
                onChange={(e) =>
                  onFieldChange("imprinting_location", e.target.value)
                }
              >
                <option value="">Select an imprinting location</option>
                {product.Imprinting?.Locations?.Values.map((location) => (
                  <option key={location.VendorCode} value={location.Name}>
                    {location.Name}
                  </option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label>Additional Imprint Notes:</label>
              <textarea
                value={formData.imprinting_decoration_instructions}
                onChange={(e) =>
                  onFieldChange("imprinting_decoration_instructions", e.target.value)
                }
                placeholder="Any additional information about the imprint"
              />
            </div>
            <div className="form-group">
              <label>Imprinting Setup Fee:</label>
              <input
                type="number"
                value={formData.imprinting_setup_fee}
                onChange={(e) =>
                  onFieldChange("imprinting_setup_fee", parseFloat(e.target.value))
                }
              />
            </div>
            <div className="form-group">
              <label>Logo Color Count:</label>
              <input
                type="number"
                value={formData.imprinting_logo_color_count}
                onChange={(e) =>
                  onFieldChange("imprinting_logo_color_count", parseInt(e.target.value, 10))
                }
              />
            </div>
            <div className="form-group">
              <label>Upload Imprint File:</label>
              <input
                type="file"
                onChange={(e) =>
                  onFieldChange("imprinting_logo", e.target.files[0])
                }
              />
            </div>
          </div>

          {/* Additional Product Details */}
          <div className="form-group additional-product-details">
            <label>Additional Product Details:</label>
            <textarea
              value={formData.additionalProductDetails}
              onChange={(e) =>
                onFieldChange("additionalProductDetails", e.target.value)
              }
              placeholder="Enter extra details for this product..."
            />
          </div>
        </>
      )}

      {/* Save Button */}
      <div className="form-group">
        <button onClick={() => handleSaveProduct(formData)}>
          Save Product
        </button>
      </div>
    </div>
  );
};

export default OrderEditExistingProductForm;
